/**
 * Refinanciamientos — pantalla unificada de los dos refinanciamientos.
 *
 * Dos tarjetas de acción abren los formularios existentes en diálogos a
 * pantalla completa: refinanciamiento de préstamos propios (ares2050,
 * cancela el préstamo activo y abre uno nuevo por el saldo) y externo
 * (ares2060, cliente que viene de otra financiera, siempre con
 * autorización). Debajo, como listado, el informe de Motores Facturados
 * embebido arrancando en tipo "Refinanciamiento" (facturas tprestamo='R').
 *
 * Sustituye las entradas de menú de refinanciamientointerno.js y
 * refinanciamientoexterno.js; ambos formularios se reutilizan.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import CurrencyExchangeOutlinedIcon from "@mui/icons-material/CurrencyExchangeOutlined";
import SwapHorizOutlinedIcon from "@mui/icons-material/SwapHorizOutlined";
import { useMaterialUIController } from "context";
import { FP, PageHeader, ActionCard } from "components/financepro";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import RefinanciamientoInterno from "modules/facturacion/refinanciamientointerno";
import RefinanciamientoExterno from "modules/facturacion/refinanciamientoexterno";
import InformeMotoresFacturadosNuevo from "modules/facturacion/informemotoresfacturados-nuevo";
import useAccionInicial from "lib/useAccionInicial";

export default function Refinanciamientos() {
  const [controller] = useMaterialUIController();
  const { roleDetalles } = controller;

  // Permisos por programa ARES (el backend re-valida en permisos.py).
  const puedeInterno = tienePermisosModulo(roleDetalles, "ares2050");
  const puedeExterno = tienePermisosModulo(roleDetalles, "ares2060");

  const [internoOpen, setInternoOpen] = useState(false);
  const [externoOpen, setExternoOpen] = useState(false);

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    interno: { permitido: puedeInterno, abrir: () => setInternoOpen(true) },
    externo: { permitido: puedeExterno, abrir: () => setExternoOpen(true) },
  });

  // Incrementarlo recarga el listado embebido (tras guardar o cerrar un refi).
  const [refreshKey, setRefreshKey] = useState(0);
  const refrescar = () => setRefreshKey((k) => k + 1);

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Refinanciamientos"
        subtitle="Refinancia préstamos propios o de otras financieras y consulta los refinanciados"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {/* Acciones principales: tarjetas grandes para iniciar un refinanciamiento */}
        {(puedeInterno || puedeExterno) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {puedeInterno && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={CurrencyExchangeOutlinedIcon}
                  titulo="Refinanciar préstamo"
                  descripcion="Cancela el préstamo actual y abre uno nuevo por el saldo acordado"
                  color={FP.color.accentBlue}
                  onClick={() => setInternoOpen(true)}
                />
              </Grid>
            )}
            {puedeExterno && (
              <Grid item xs={12} sm={6}>
                <ActionCard
                  icon={SwapHorizOutlinedIcon}
                  titulo="Refinanciamiento externo"
                  descripcion="Cliente de otra financiera: préstamo sobre un chasis disponible"
                  color={FP.color.info}
                  onClick={() => setExternoOpen(true)}
                />
              </Grid>
            )}
          </Grid>
        )}

        {/* Listado: el informe de facturados embebido, filtrado a refinanciamientos */}
        <InformeMotoresFacturadosNuevo embedded refreshToken={refreshKey} tipoInicial="Refinanciamiento" />
      </Box>

      {/* Refinanciamiento de préstamos (formulario completo a pantalla completa) */}
      <Dialog fullScreen open={internoOpen} onClose={() => setInternoOpen(false)}>
        <RefinanciamientoInterno embedded onExit={() => { setInternoOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>

      {/* Refinanciamiento externo (formulario completo a pantalla completa) */}
      <Dialog fullScreen open={externoOpen} onClose={() => setExternoOpen(false)}>
        <RefinanciamientoExterno embedded onExit={() => { setExternoOpen(false); refrescar(); }} onSaved={refrescar} />
      </Dialog>
    </Box>
  );
}
